import { Injectable, inject, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { ChargeItemDefinition, Bundle } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { HttpContext, HttpClient } from '@angular/common/http';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';
import { UtilityService } from '../shared/utility.service';
import { StateService } from '../shared/state.service';
import { Subject, takeUntil, expand, EMPTY, reduce, map, Observable, of, tap, switchMap } from 'rxjs';

export interface ChargeRow extends TableRowModel {
  rawResource: ChargeItemDefinition;
  title: string;
  codeDisplay: string;
  basePriceDisplay: string;
}

@Injectable({ providedIn: 'root' })
export class ChargesSearchService implements OnDestroy {
  private readonly tableFiltering = inject<TableFilteringService<ChargeItemDefinition, ChargeRow>>(TableFilteringService);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly utility = inject(UtilityService);
  private readonly stateService = inject(StateService);
  private readonly http = inject(HttpClient);
  private readonly silentContext = new HttpContext().set(LoadingUIEnabled, false);
  private readonly destroy$ = new Subject<void>();

  private readonly internalDataSource = new MatTableDataSource<ChargeRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly displayedTotal$ = this.tableFiltering.displayedTotalSubject;
  readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
  readonly error$ = this.tableFiltering.error$;

  // We track the currently cached statuses to avoid redundant count queries
  private fullyFetchedStatuses = new Set<string>();

  init(options: { pageSize?: number } = {}): void {
    const pageSize = options.pageSize ?? 25;
    
    this.tableFiltering.init({
      resourceType: 'ChargeItemDefinition',
      defaultQuery: '/ChargeItemDefinition',
      dataSource: this.internalDataSource,
      baseUrl: this.backendUrl,
      pageSize,
      enableLocalFiltering: true,
      localFilterPredicate: (row, filters) => this.filterPredicate(row, filters),
      httpOptions: { context: this.silentContext.set(DisableCountParam, true) }
    });

    // Populate initial local rows from state
    this.updateLocalRowsFromState();

    // Listen to state changes to update rows if they change globally
    this.stateService.orgWideResources.chargeItemDefinitions
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateLocalRowsFromState();
      });
  }

  private updateLocalRowsFromState(): void {
    const allLocal = this.stateService.orgWideResources.chargeItemDefinitions.getValue();
    const rows = allLocal.map(item => this.transformEntry(item.actualResource));
    this.tableFiltering.setLocalRows(rows, true);
  }

  get dataSource(): MatTableDataSource<ChargeRow> {
    return this.internalDataSource;
  }

  search(filters: TableFilters): void {
    const status = (filters['status'] as string) || 'active';
    
    // If we've already fetched everything for this status, skip backend
    if (this.fullyFetchedStatuses.has(status)) {
       this.tableFiltering.search(filters);
       return;
    }

    // Otherwise, check backend count vs local count for this status
    this.http.get<Bundle>(`${this.backendUrl}/ChargeItemDefinition?status=${status}&_summary=count`, {
      context: this.silentContext
    }).pipe(
      switchMap(bundle => {
        const backendCount = bundle.total || 0;
        const allLocal = this.stateService.orgWideResources.chargeItemDefinitions.getValue();
        const localWithStatus = allLocal.filter(x => x.actualResource?.status === status);

        if (localWithStatus.length >= backendCount) {
          // We have them all, mark as fetched
          this.fullyFetchedStatuses.add(status);
          return of(null);
        } else {
          // We need to fetch all pages for this status
          return this.fetchAllPages(status).pipe(
            tap(() => this.fullyFetchedStatuses.add(status))
          );
        }
      })
    ).subscribe({
      next: () => {
        // The fetch (if any) will have updated state, which updates localRows, 
        // so we just trigger the search pipeline
        this.tableFiltering.search(filters);
      },
      error: (err) => {
        console.error('Failed to sync charges:', err);
        // Fallback to searching whatever we have locally
        this.tableFiltering.search(filters);
      }
    });
  }

  private fetchAllPages(status: string): Observable<void> {
    const initialUrl = `${this.backendUrl}/ChargeItemDefinition?status=${status}&_count=500`;
    
    return this.http.get<Bundle>(initialUrl).pipe(
      expand((bundle) => {
        const nextLink = bundle.link?.find((l) => l.relation === 'next')?.url;
        if (nextLink) {
          const finalUrl = nextLink.startsWith('http') ? nextLink : `${this.backendUrl}/${nextLink.startsWith('/') ? nextLink.slice(1) : nextLink}`;
          return this.http.get<Bundle>(finalUrl);
        }
        return EMPTY;
      }),
      tap((bundle) => {
        this.stateService.processOrgWideBundleTransaction(bundle);
      }),
      reduce((acc) => acc, null),
      map(() => void 0)
    );
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(): void {
    this.fullyFetchedStatuses.clear();
    const currentFilters = (this.tableFiltering as any).filtersSubject?.getValue() || {};
    this.search(currentFilters);
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.tableFiltering.destroy();
    this.internalDataSource.disconnect();
    this.internalDataSource.data = [];
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private transformEntry(resource: ChargeItemDefinition): ChargeRow {
    const fallbackId = resource.id ?? `ChargeItemDefinition-${Date.now()}`;
    
    let codeDisplay = 'N/A';
    if (resource.code) {
        codeDisplay = resource.code.text || resource.code.coding?.[0]?.display || resource.code.coding?.[0]?.code || 'N/A';
    } else if (resource.instance && resource.instance.length > 0) {
        codeDisplay = resource.instance[0].display || resource.instance[0].reference || 'N/A';
    }

    const salesComponents = this.utility.seperateTotalPriceComponents(resource);
    const saleSymbol = this.utility.getCurrencySymbol(salesComponents?.currency || (resource.propertyGroup?.[0]?.priceComponent?.[0]?.amount?.currency || 'NGN'));
    const basePriceDisplay = `${saleSymbol}${salesComponents?.totalSellingPrice || 0}`;

    return {
      ...resource,
      id: fallbackId,
      resourceType: 'ChargeItemDefinition',
      display: resource.title || codeDisplay,
      rawResource: resource,
      title: resource.title || codeDisplay,
      codeDisplay,
      basePriceDisplay
    };
  }

  private filterPredicate(row: ChargeRow, filters: TableFilters): boolean {
    const searchText = ((filters['searchtext'] as string) || '').toLowerCase();
    const tab = ((filters['tab'] as string) || 'all');
    const status = ((filters['status'] as string) || 'active');

    // 1. Status Filter
    if (row.rawResource.status !== status) {
      return false;
    }

    // 2. Tab Filter (Products vs Services)
    if (tab === 'services') {
      // Must not be an instance-based charge
      if ((row.rawResource.instance?.length ?? 0) > 0) return false;
    } else if (tab === 'products') {
      // Must be an instance-based charge
      if ((row.rawResource.instance?.length ?? 0) === 0) return false;
    }

    // 3. Text Search Filter
    if (searchText) {
      const titleMatch = (row.title || '').toLowerCase().includes(searchText);
      const codeMatch = (row.codeDisplay || '').toLowerCase().includes(searchText);
      if (!titleMatch && !codeMatch) {
        return false;
      }
    }

    return true;
  }
}
